import React from "react"
import styled from "styled-components"
import { IoIosPin } from "react-icons/io"
import { Link } from "react-router-dom"
import { Header } from "../Components/Header/Header"
import { ColumnDef, DataTable } from "../Components/Table/DataTable"
import useGetCustomData from "../Hooks/useGetCustomData"
import { PageContainer } from "../Styles/AppStyles"
import { COLORS } from "../Styles/Colors"
import { USER_ROLE } from "../Types"
import { useEffect, useMemo, useState } from "react"
import { LocationSelect } from "../Components/Select/LocationSelect"
import { decode } from "../Services/DecodeToken"
import { formatDateOnly } from "../Services/FormatUtc"
import dayjs from "dayjs"
import { DateRangePicker } from "../Components/Date/Datepicker"
import { useColumnSearches } from "../Hooks/useColumnSearch"
import { SearchableHeader } from "../Components/Search/SearchableHeader"
import { AssetTransactionMergeExportDialog } from "./Assets/AssetTransactionMergeExportDialog"

const TypedIoIosPin = IoIosPin as unknown as React.FC<{ size?: number | string }>;

const TrackIconWrapper = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  cursor: pointer;
  width: 100%;
  height: 100%;
  & svg {
    color: ${COLORS.teal};
    font-size: 1.5em;
  }
`;




export const AssetTransactionPage = () => {

    const [ovs] = useGetCustomData('/api/location/user/ovs');
    const [selectedOvs, setSelectedOvs] = useState<string>('');

    const [startDate, setStartDate] = useState<Date | null>(null);
    const [endDate, setEndDate] = useState<Date | null>(null);
    const [page, setPage] = useState(0);
    const [pageSize, setPageSize] = useState(50);
    const [sortField, setSortField] = useState<string>('');
    const [sortOrder, setSortOrder] = useState<'asc' | 'desc'>('desc');
    const [mergeExportOpen, setMergeExportOpen] = useState(false);

    const token: any = decode();

    useEffect(() => {
        if (
            token.roles.id !== USER_ROLE.ADMIN_ABATIX &&
            ovs.length > 0 &&
            !selectedOvs
        ) {
            setSelectedOvs(ovs[0].OVID);
        }
    }, [ovs, selectedOvs]);

    const searchFields = [
        'name',
        'part_number',
        'serial_number',
        'barcode',
        'manufacturer',
        'manufacturer_id',
        'po_number',
        'company_name',
        'location_name',
        'checkout_user_name',
        'checkout_requester_name',
        'issued_bin_location_name',
        'checkin_user_name',
        'checkin_requester_name',
        'returned_bin_location_name',
    ];

    const { columnSearches, setSearch, clearSearch } = useColumnSearches(searchFields);

    const handleColumnSearch = (field: string, value: string) => {
        setSearch(field, value);
        setPage(0);
    };

    const handleClearColumnSearch = (field: string) => {
        clearSearch(field);
        setPage(0);
    };

    const handleSortChange = (field: string, order: 'asc' | 'desc' | null) => {
        setSortField(field);
        setSortOrder(order ?? 'desc');
        setPage(0);
    };

    const baseUrl = useMemo(() => {
        const params = new URLSearchParams();

        if (selectedOvs && selectedOvs !== '') {
            params.set('ovid', selectedOvs);
        }

        if (startDate) params.set('startDate', dayjs(startDate).format('YYYY-MM-DD'));
        if (endDate) params.set('endDate', dayjs(endDate).format('YYYY-MM-DD'));

        Object.entries(columnSearches).forEach(([field, value]) => {
            if (value.trim()) params.set(field, value.trim());
        });

        if (sortField) {
            params.set('sort_field', sortField);
            params.set('sort_order', sortOrder);
        }

        return `/api/assets/transaction?${params.toString()}`;
    }, [selectedOvs, startDate, endDate, columnSearches, sortField, sortOrder]);

    useEffect(() => { setPage(0); }, [selectedOvs, startDate, endDate]);

    const paginatedUrl = `${baseUrl}&limit=${pageSize}&offset=${page * pageSize}`;
    const [assetTransaction, loadingAsset, , refetch] = useGetCustomData(paginatedUrl);

    const assetTransactionColumns: ColumnDef<any>[] = [
        {
            field: 'company_name',
            headerName: 'Account',
            width: 160,
            sortable: true,
            renderHeader: () => <SearchableHeader field='company_name' headerName='Account' searchValue={columnSearches.company_name} onSearchChange={handleColumnSearch} onClearSearch={handleClearColumnSearch} />,
        },
        {
            field: 'location_name',
            headerName: 'Location',
            width: 140,
            sortable: true,
            renderHeader: () => <SearchableHeader field='location_name' headerName='Location' searchValue={columnSearches.location_name} onSearchChange={handleColumnSearch} onClearSearch={handleClearColumnSearch} />,
        },
        {
            field: 'part_number',
            headerName: 'Part No.',
            width: 130,
            sortable: true,
            renderHeader: () => <SearchableHeader field='part_number' headerName='Part No.' searchValue={columnSearches.part_number} onSearchChange={handleColumnSearch} onClearSearch={handleClearColumnSearch} />,
        },
        {
            field: 'name',
            headerName: 'Description',
            width: 200,
            sortable: true,
            renderHeader: () => <SearchableHeader field='name' headerName='Description' searchValue={columnSearches.name} onSearchChange={handleColumnSearch} onClearSearch={handleClearColumnSearch} />,
        },
        {
            field: 'serial_number',
            headerName: 'Serial No.',
            width: 147,
            sortable: true,
            renderHeader: () => <SearchableHeader field='serial_number' headerName='Serial No.' searchValue={columnSearches.serial_number} onSearchChange={handleColumnSearch} onClearSearch={handleClearColumnSearch} />,
        },
        {
            field: 'barcode',
            headerName: 'Barcode',
            width: 160,
            sortable: true,
            renderHeader: () => <SearchableHeader field='barcode' headerName='Barcode' searchValue={columnSearches.barcode} onSearchChange={handleColumnSearch} onClearSearch={handleClearColumnSearch} />,
        },
        {
            field: 'manufacturer',
            headerName: 'Manufacturer',
            width: 160,
            sortable: true,
            renderHeader: () => <SearchableHeader field='manufacturer' headerName='Manufacturer' searchValue={columnSearches.manufacturer} onSearchChange={handleColumnSearch} onClearSearch={handleClearColumnSearch} />,
        },
        {
            field: 'manufacturer_id',
            headerName: 'Manufacturer ID',
            width: 160,
            sortable: true,
            renderHeader: () => <SearchableHeader field='manufacturer_id' headerName='Manufacturer ID' searchValue={columnSearches.manufacturer_id} onSearchChange={handleColumnSearch} onClearSearch={handleClearColumnSearch} />,
        },
        {
            field: 'po_number',
            headerName: 'PO/Job No.',
            width: 130,
            sortable: true,
            renderHeader: () => <SearchableHeader field='po_number' headerName='PO/Job No.' searchValue={columnSearches.po_number} onSearchChange={handleColumnSearch} onClearSearch={handleClearColumnSearch} />,
        },
        { field: 'checkout_datetime', headerName: 'Checkout Date', width: 124, sortable: true, renderCell: (params) => formatDateOnly(params.row.checkout_datetime) },
        {
            field: 'checkout_user_name',
            headerName: 'Checkout Employee',
            width: 163,
            sortable: true,
            renderHeader: () => <SearchableHeader field='checkout_user_name' headerName='Checkout Employee' searchValue={columnSearches.checkout_user_name} onSearchChange={handleColumnSearch} onClearSearch={handleClearColumnSearch} />,
        },
        {
            field: 'checkout_requester_name',
            headerName: 'Checkout Requester',
            width: 168,
            sortable: true,
            renderHeader: () => <SearchableHeader field='checkout_requester_name' headerName='Checkout Requester' searchValue={columnSearches.checkout_requester_name} onSearchChange={handleColumnSearch} onClearSearch={handleClearColumnSearch} />,
        },
        {
            field: 'issued_bin_location_name',
            headerName: 'Checkout Bin',
            width: 131,
            sortable: true,
            renderHeader: () => <SearchableHeader field='issued_bin_location_name' headerName='Checkout Bin' searchValue={columnSearches.issued_bin_location_name} onSearchChange={handleColumnSearch} onClearSearch={handleClearColumnSearch} />,
        },
        { field: 'checkin_datetime', headerName: 'Check-in Date', width: 120, sortable: true, renderCell: (params) => formatDateOnly(params.row.checkin_datetime) },
        {
            field: 'checkin_user_name',
            headerName: 'Check-in Employee',
            width: 158,
            sortable: true,
            renderHeader: () => <SearchableHeader field='checkin_user_name' headerName='Check-in Employee' searchValue={columnSearches.checkin_user_name} onSearchChange={handleColumnSearch} onClearSearch={handleClearColumnSearch} />,
        },
        {
            field: 'checkin_requester_name',
            headerName: 'Check-in Requester',
            width: 180,
            sortable: true,
            renderHeader: () => <SearchableHeader field='checkin_requester_name' headerName='Check-in Requester' searchValue={columnSearches.checkin_requester_name} onSearchChange={handleColumnSearch} onClearSearch={handleClearColumnSearch} />,
        },
        {
            field: 'returned_bin_location_name',
            headerName: 'Check-in Bin',
            width: 150,
            sortable: true,
            renderHeader: () => <SearchableHeader field='returned_bin_location_name' headerName='Check-in Bin' searchValue={columnSearches.returned_bin_location_name} onSearchChange={handleColumnSearch} onClearSearch={handleClearColumnSearch} />,
        },
        { field: 'note', headerName: 'Note', width: 200 },
        { field: 'is_active', headerName: 'Active', width: 100, renderCell: (params) => params.value ? 'Y' : 'N' },
        { field: 'transaction_number', headerName: 'Transaction No.', width: 150 },
        { field: 'created_at', headerName: 'Created At', width: 150, renderCell: (params) => formatDateOnly(params.row.created_at) },
        { field: 'updated_at', headerName: 'Updated At', width: 150, renderCell: (params) => formatDateOnly(params.row.updated_at) },
        {
            field: 'track',
            headerName: 'Track',
            width: 100,
            sortable: false,
            filterable: false,
            renderCell: (params) => {
                const deviceId = params.row.gps_device_id;
                if (!deviceId) return null;
                return (
                    <Link
                        to={`/maps?deviceId=${deviceId}`}
                        title="View GPS Tracking"
                        style={{
                            display: 'flex',
                            width: '100%',
                            height: '100%',
                            justifyContent: 'center',
                            alignItems: 'center',
                        }}
                    >
                        <TrackIconWrapper>
                            <TypedIoIosPin />
                        </TrackIconWrapper>
                    </Link>
                );
            },
        },
    ];

    const totalRows = assetTransaction?.totalCount ?? 0;
    const assetTransactionData = assetTransaction?.data ?? [];

    return (
        <PageContainer>
            <Header title="Asset Transactions" count={totalRows} hideCount={false}>
                <DateRangePicker startDate={startDate} endDate={endDate} setStartDate={setStartDate} setEndDate={setEndDate} />
            </Header>

            <DataTable
                columns={assetTransactionColumns}
                data={assetTransactionData}
                loading={loadingAsset}
                pageKey="asset-transactions-v2"
                defaultColumnVisibility={{
                    company_name: false,
                    barcode: false,
                    manufacturer: false,
                    manufacturer_id: false,
                    is_active: false,
                    transaction_number: false,
                    created_at: false,
                    updated_at: false,
                }}
                idField="id"
                serverSidePagination={true}
                page={page}
                rowCount={totalRows}
                onPageChange={setPage}
                onPageSizeChange={setPageSize}
                initialPageSize={pageSize}
                hideColumnFilters={true}
                hideSearchBar={true}
                serverSideSort={true}
                onSortModelChange={handleSortChange}
                onMergeExport={() => setMergeExportOpen(true)}
            >
                <LocationSelect
                    ovs={ovs}
                    selectedOvs={selectedOvs}
                    onChange={setSelectedOvs}
                />
            </DataTable>

            <AssetTransactionMergeExportDialog
                open={mergeExportOpen}
                onClose={() => setMergeExportOpen(false)}
                initialFilters={{
                    startDate,
                    endDate,
                    selectedOvs,
                    columnSearches,
                }}
            />
        </PageContainer>
    )
}
